import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Box,
  Button,
  Divider,
  Flex,
  Icon,
  Link,
  Skeleton,
  Table,
  TableContainer,
  Tag,
  TagLabel,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
  useColorModeValue,
  useToast,
  VStack
} from "@chakra-ui/react";
import { yupResolver } from "@hookform/resolvers/yup";
import { BackButton } from "components/Atomos/Buttons/BackButton";
import { InputFileAnexo } from "components/Atomos/Form/Elements/InputFileAnexo";
import { InputText } from "components/Atomos/Form/Elements/InputText";
import { StatusRequerimentoTag } from "components/Atomos/Tags/StatusRequerimentoTag";
import { Title } from "components/Atomos/Texts/Title";
import { ConsultaPreviaButton } from "components/Moleculas/ConsultaPreviaButton";
import { useEffect, useMemo, useState } from "react";
import {
  FormProvider,
  SubmitHandler,
  useForm,
  useFormContext
} from "react-hook-form";
import {
  RiCheckLine,
  RiDownloadLine,
  RiErrorWarningLine
} from "react-icons/ri";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useAppDispatch, useTypedSelector } from "store/hooks";
import { downloadArquivoEtapaSolicitacao } from "store/modules/licenciamentourbano/actions/downloadArquivoEtapaSolicitacao";
import { obterDetalheTipoRequerimento } from "store/modules/licenciamentourbano/actions/obterDetalheTipoRequerimento";
import {
  DetalheQuestao,
  DetalheSecao
} from "store/modules/licenciamentourbano/actions/obterDetalheTipoRequerimento";
import { FormFields } from "pages/LicenciamentoUrbano/SolicitarRequerimento/components/FormRequerimento";
import {
  acharQuestao,
  filtrarGuidsValidos,
  parseValorAtual,
  serializarValorCorrigido,
  ValoresCampo
} from "utils/camposPendencia";
import { downloadArquivoPendencia } from "store/modules/licenciamentourbano/actions/downloadArquivoPendencia";
import { downloadArquivoRespostaPendencia } from "store/modules/licenciamentourbano/actions/downloadArquivoRespostaPendencia";
import { downloadArquivoSolicitacao } from "store/modules/licenciamentourbano/actions/downloadArquivoSolicitacao";
import { obterDetalheSolicitacao } from "store/modules/licenciamentourbano/actions/obterDetalheSolicitacao";
import { rotuloOcorrenciaSecao } from "utils/secaoTemplateMeta";
import { responderPendencia } from "store/modules/licenciamentourbano/actions/responderPendencia";
import { directDownloadFileUrl } from "utils/fileHelper";
import { getSituacaoPendenciaInfo } from "utils/situacao-pendencia";
import { formatarProtocolo } from "utils/protocolo";
import * as yup from "yup";

export const DetalharRequerimentoInicio = () => {
  const { id } = useParams<{ id: string }>();
  const dispatch = useAppDispatch();
  const location = useLocation();
  const navigate = useNavigate();

  // Confirmação de envio: o formulário manda `recemCriada` no state da rota.
  // Guardamos em estado local e limpamos o state em seguida, senão o alerta
  // voltaria a cada refresh (o state da rota sobrevive ao reload).
  const [recemCriada, setRecemCriada] = useState(false);

  useEffect(() => {
    const state = location.state as { recemCriada?: boolean } | null;
    if (!state?.recemCriada) return;
    setRecemCriada(true);
    navigate(location.pathname, { replace: true });
  }, [location.state]);

  // Rótulo do grupo de documentos (enunciado) legível no dark mode.
  const enunciadoColor = useColorModeValue("gray.600", "gray.300");

  const { successPayload: solicitacao, isRequesting } = useTypedSelector(
    (state) => state.licenciamentourbano.obterDetalheSolicitacao
  );

  const { isRequesting: isRequestingDownloadPendencia } = useTypedSelector(
    (state) => state.licenciamentourbano.downloadArquivoPendencia
  );

  const { isRequesting: isRequestingDownloadRespostaPendencia } =
    useTypedSelector(
      (state) => state.licenciamentourbano.downloadArquivoRespostaPendencia
    );

  const { isRequesting: isRequestingDownloadSolicitacao } = useTypedSelector(
    (state) => state.licenciamentourbano.downloadArquivoSolicitacao
  );

  useEffect(() => {
    if (id) {
      dispatch(obterDetalheSolicitacao({ idSolicitacao: id }));
    }
  }, [id]);

  // PBI 2576 — a estrutura do tipo de requerimento traz as OPÇÕES de select e
  // o tipoSecao (máscaras), que o payload da pendência não traz. Só busca
  // quando há de fato campo a corrigir, para não pesar a tela à toa.
  const idTipoRequerimento = solicitacao?.idTipoRequerimento;
  const precisaEstruturaDoFormulario = !!solicitacao?.pendencias?.some(
    (p) =>
      p.situacao?.value === "AGUARDANDO_INFORMACAO" &&
      !!p.camposSolicitacao?.length
  );

  useEffect(() => {
    if (idTipoRequerimento && precisaEstruturaDoFormulario) {
      dispatch(obterDetalheTipoRequerimento({ idTipoRequerimento }));
    }
  }, [idTipoRequerimento, precisaEstruturaDoFormulario]);

  function baixarArquivoSolicitacao(idArquivo: string) {
    dispatch(
      downloadArquivoSolicitacao({
        idArquivo,
        onSuccess: (arquivo) => {
          directDownloadFileUrl(arquivo.caminho, arquivo.originalFileName);
        }
      })
    );
  }

  function baixarArquivoPendencia(idArquivo: string) {
    dispatch(
      downloadArquivoPendencia({
        idArquivo,
        onSuccess: (arquivo) => {
          directDownloadFileUrl(arquivo.caminho, arquivo.originalFileName);
        }
      })
    );
  }

  function baixarArquivoRespostaPendencia(idArquivo: string) {
    dispatch(
      downloadArquivoRespostaPendencia({
        idArquivo,
        onSuccess: (arquivo) => {
          directDownloadFileUrl(arquivo.caminho, arquivo.originalFileName);
        }
      })
    );
  }

  if (!solicitacao && !isRequesting) return <p>Solicitação não encontrada</p>;

  return (
    <Flex
      justifyContent="center"
      alignItems="center"
      bgColor="graybase.500"
      direction="column"
      overflowY="auto"
      sx={{ scrollBehavior: "smooth" }}
    >
      <Flex
        flex={1}
        pb="12"
        direction="column"
        maxW="container.xl"
        w="full"
        pt={{ base: 8 }}
        px={{ base: 6, sm: 8, xl: 16 }}
      >
        <BackButton to="/" />
        <Box my="6">
          <Title>Detalhes do Requerimento</Title>
        </Box>
        {/* Só aparece com o protocolo em mãos: ele chega junto com o detalhe. */}
        {recemCriada && solicitacao?.protocolo && (
          <Alert status="success" borderRadius="lg" mb="6" alignItems="start">
            <AlertIcon />
            <Box>
              <AlertTitle>Solicitação enviada.</AlertTitle>
              <AlertDescription>
                Guarde o protocolo{" "}
                <Text as="strong">
                  {formatarProtocolo(solicitacao.protocolo)}
                </Text>{" "}
                para acompanhar.
              </AlertDescription>
            </Box>
          </Alert>
        )}
        <Flex gap="4" direction={{ base: "column", lg: "row" }}>
          <Box flex={0.5}>
            <Box borderRadius="lg" bgColor="white" boxShadow="sm" p="6">
              <Title as="h4">Informações gerais</Title>
              {isRequesting ? (
                <Skeleton h="120px" />
              ) : (
                <VStack spacing={2} mt="2">
                  <Text w="full">
                    <Text as="strong" fontWeight="medium">
                      Protocolo:{" "}
                    </Text>
                    <Text as="span">
                      {formatarProtocolo(solicitacao?.protocolo)}
                    </Text>
                  </Text>
                  <Text w="full">
                    <Text as="strong" fontWeight="medium">
                      Tipo de Solicitação:{" "}
                    </Text>
                    <Text as="span">{solicitacao?.nomeRequerimento}</Text>
                  </Text>
                  <Text w="full">
                    <Text as="strong" fontWeight="medium">
                      Data de Envio:{" "}
                    </Text>
                    <Text as="span">{solicitacao?.dataEnvioFormatada}</Text>
                  </Text>
                  <Text w="full">
                    <Text as="strong" fontWeight="medium">
                      Status Atual:{" "}
                    </Text>
                    {solicitacao?.status && (
                      <StatusRequerimentoTag
                        nome={solicitacao?.status?.name}
                        id={solicitacao?.status?.value?.toString()}
                      />
                    )}
                  </Text>
                  <Text w="full">
                    <Text as="strong" fontWeight="medium">
                      Última Atualização:{" "}
                    </Text>
                    <Text as="span">
                      {solicitacao?.dataUltimaAtualizacaoFormatada}
                    </Text>
                  </Text>
                </VStack>
              )}
            </Box>
            <Box borderRadius="xl" bgColor="white" boxShadow="sm" p="6" mt="4">
              <Text fontSize="1.2rem" fontWeight="medium" mb="4">
                Etapas do processo
              </Text>
              {solicitacao?.etapas.map((e, i) => (
                <EtapaProcesso
                  key={e.idEtapa}
                  etapa={e.ordem}
                  status={
                    e.isConcluida
                      ? "concluido"
                      : e.isAtual
                      ? "atual"
                      : "pendente"
                  }
                  titulo={e.nome}
                  data={e.dataConclusaoFormatada}
                  equipe={e.equipe}
                  last={i === solicitacao?.etapas.length - 1}
                  observacao={e.observacao}
                  arquivos={e.arquivosConclusao}
                  situacaoEtapa={e.situacaoEtapaSolicitacao}
                />
              ))}
            </Box>
          </Box>
          <Box borderRadius="lg" bgColor="white" boxShadow="sm" p="6" flex={1}>
            {solicitacao?.pendencias && solicitacao.pendencias.length > 0 && (
              <Box
                bgColor={
                  // Vermelho só quando há pendência AGUARDANDO INFORMAÇÃO (o cidadão
                  // precisa responder); "Informação Recebida"/"Resolvida" → fundo neutro.
                  solicitacao?.pendencias?.some(
                    (x) => x.situacao?.value === "AGUARDANDO_INFORMACAO"
                  )
                    ? "red.100"
                    : "gray.100"
                }
                py="4"
                px="6"
                mb="5"
                borderRadius="lg"
              >
                <Text fontWeight="medium" fontSize="lg" mb="2">
                  Pendências ou Observações
                </Text>
                {solicitacao?.pendencias.map((pendencia) => (
                  <Flex
                    id={`pendencia-${pendencia.id}`}
                    key={pendencia.id}
                    borderRadius="lg"
                    bgColor="white"
                    px="4"
                    py="2"
                    mb={3}
                    _last={{ mb: 0 }}
                    sx={{ scrollMarginTop: "1rem" }}
                  >
                    <Box flex={1}>
                      <PendenciaSituacaoInfo
                        situacao={pendencia.situacao?.value}
                      />
                      <Text w="full" mb="1">
                        <Text as="strong" fontWeight="medium">
                          Observação:{" "}
                        </Text>
                        <Text as="span">{pendencia.comentario}</Text>
                      </Text>
                      {pendencia.respondidoPor && (
                        <Text w="full" fontSize="sm" color="gray.600">
                          <Text as="strong" fontWeight="medium">
                            Respondido por:{" "}
                          </Text>
                          <Text as="span">{pendencia.respondidoPor}</Text>
                        </Text>
                      )}
                      {pendencia.resolvidoPor && (
                        <Text w="full" fontSize="sm" color="gray.600">
                          <Text as="strong" fontWeight="medium">
                            Resolvido por:{" "}
                          </Text>
                          <Text as="span">{pendencia.resolvidoPor}</Text>
                        </Text>
                      )}

                      {pendencia.arquivosSolicitacaoVinculados &&
                        pendencia.arquivosSolicitacaoVinculados.length > 0 && (
                          <Box mb="2">
                            <Text as="strong" fontWeight="medium" fontSize="sm">
                              Documento(s) relacionado(s):{" "}
                            </Text>
                            <Flex direction="column" gap="1" mt="1">
                              {pendencia.arquivosSolicitacaoVinculados.map(
                                (arq) => (
                                  <Link
                                    key={arq.id}
                                    href={`#arquivo-${arq.id}`}
                                    display="inline-flex"
                                    alignItems="center"
                                    gap="1"
                                    fontSize="sm"
                                    color="red.500"
                                    w="fit-content"
                                  >
                                    <RiErrorWarningLine size={14} />
                                    {arq.nomeOriginal}
                                  </Link>
                                )
                              )}
                            </Flex>
                          </Box>
                        )}

                      {pendencia.arquivos?.map((f) => (
                        <Button
                          key={f.id}
                          variant="solid"
                          colorScheme="gray"
                          fontSize="sm"
                          py="1"
                          w="full"
                          justifyContent="start"
                          size="sm"
                          onClick={() => baixarArquivoPendencia(f.id)}
                          isLoading={isRequestingDownloadPendencia.includes(
                            f.id
                          )}
                        >
                          <RiDownloadLine size={16} />
                          <Text ml="1" as="span">
                            {f.originalFileName}
                          </Text>
                        </Button>
                      ))}
                      <Divider my="4" />
                      {pendencia.respostas.map((resposta) => (
                        <Flex
                          key={
                            (resposta.dataCriacao || "") +
                            (resposta.comentario || "")
                          }
                          direction="column"
                          alignItems={
                            resposta.isColaborador ? "flex-start" : "end"
                          }
                          mb="4"
                        >
                          <Text
                            bgColor={
                              resposta.isColaborador ? "gray.500" : "green.600"
                            }
                            py="2"
                            px="4"
                            borderRadius="lg"
                            color="white"
                          >
                            {resposta.comentario}
                          </Text>
                          {resposta.arquivos?.map((f) => (
                            <Button
                              key={f.id}
                              variant="link"
                              fontSize="sm"
                              py="1"
                              onClick={() =>
                                baixarArquivoRespostaPendencia(f.id)
                              }
                              isLoading={isRequestingDownloadRespostaPendencia.includes(
                                f.id
                              )}
                            >
                              <RiDownloadLine size={16} />
                              <Text ml="1" as="span">
                                {f.originalFileName}
                              </Text>
                            </Button>
                          ))}
                          <Text fontSize="sm">
                            <Text as="span" fontWeight="medium">
                              {resposta.responsavel}
                            </Text>{" "}
                            <Text as="span" fontSize="xs" color="gray.600">
                              {resposta.dataCriacaoFormatada}
                            </Text>
                          </Text>
                        </Flex>
                      ))}
                      {pendencia.situacao?.value === "AGUARDANDO_INFORMACAO" &&
                        id && (
                          <Box mt="2" w="full">
                            <FormPendencia
                              key={`${pendencia.id}-${pendencia.situacao?.value}`}
                              idPendencia={pendencia.id}
                              idSolicitacao={id}
                              campos={pendencia.camposSolicitacao}
                            />
                          </Box>
                        )}
                    </Box>
                  </Flex>
                ))}
              </Box>
            )}
            <Title as="h4">Informações fornecidas</Title>
            {isRequesting ? (
              <Skeleton h="40px" />
            ) : solicitacao?.informacoesRequerimentos?.length ? (
              <Box mt="2">
                <Text fontWeight="medium" fontSize="xl" mb="2">
                  Informações do projeto
                </Text>
                <VStack spacing={2}>
                  {solicitacao?.informacoesRequerimentos.map(
                    (secao, idxSecao) => (
                      <Box
                        key={secao.idSecao ?? `secao-${idxSecao}`}
                        mb="2"
                        w="full"
                      >
                        <Text fontWeight="medium" mb="1">
                          {secao.nomeSecao}
                        </Text>
                        <VStack spacing={2}>
                          {secao.repeticoes.map((rep) => (
                            <Box
                              key={rep.indice}
                              w="full"
                              py="2"
                              px="4"
                              rounded="xl"
                              border="1px solid"
                              borderColor="gray.300"
                            >
                              {secao.repetivel && (
                                <Text fontSize="sm" fontWeight="medium" mb={1}>
                                  {rotuloOcorrenciaSecao(
                                    secao.tipoSecao,
                                    secao.nomeSecao,
                                    rep.indice + 1
                                  )}
                                </Text>
                              )}
                              {rep.respostas.map((resposta, idxResposta) => (
                                <Flex
                                  key={`${rep.indice}-${idxResposta}`}
                                  w="full"
                                  justifyContent="space-between"
                                  alignItems="center"
                                  gap={2}
                                >
                                  <Text flex={1} fontSize="sm">
                                    <Text as="strong" fontWeight="medium">
                                      {resposta.enunciado}:{" "}
                                    </Text>
                                    <Text as="span">{resposta.resposta}</Text>
                                  </Text>
                                  <ConsultaPreviaButton
                                    tipo={resposta.tipo}
                                    resposta={resposta.resposta}
                                  />
                                </Flex>
                              ))}
                            </Box>
                          ))}
                        </VStack>
                      </Box>
                    )
                  )}
                </VStack>
              </Box>
            ) : null}
            <Divider my="5" />
            {solicitacao?.arquivos && solicitacao?.arquivos.length > 0 && (
              <Box>
                <Text fontWeight="medium" fontSize="xl" mb="2">
                  Documentação anexada
                </Text>
                {/* Agrupado pelo campo do formulário (enunciado, 2505). Usa
                    `arquivosAgrupados` do backend; se vier vazio, cai para um
                    único grupo sem rótulo com a lista plana `arquivos`. Dentro
                    do grupo, arquivo vinculado a pendência vira link (develop). */}
                <Flex direction="column" gap={4}>
                  {(solicitacao.arquivosAgrupados?.length
                    ? solicitacao.arquivosAgrupados
                    : [
                        {
                          enunciado: "",
                          ordem: 0,
                          arquivos: solicitacao.arquivos
                        }
                      ]
                  ).map((grupo, indiceGrupo) => (
                    <Box key={`${grupo.enunciado}-${indiceGrupo}`}>
                      {grupo.enunciado && (
                        <Text
                          fontSize="sm"
                          fontWeight="semibold"
                          color={enunciadoColor}
                          mb={1}
                          noOfLines={2}
                          wordBreak="break-word"
                          title={grupo.enunciado}
                        >
                          {grupo.enunciado}
                        </Text>
                      )}
                      <TableContainer>
                        <Table variant="simple" size="sm">
                          <Thead>
                            <Tr>
                              <Th w="full">Nome do arquivo</Th>
                              <Th>Visualizar</Th>
                            </Tr>
                          </Thead>
                          <Tbody>
                            {grupo.arquivos.map((a) => (
                              <Tr
                                key={a.id}
                                id={`arquivo-${a.id}`}
                                sx={{ scrollMarginTop: "1rem" }}
                              >
                                <Td w="full">
                                  {a.idPendenciaVinculada ? (
                                    <Tooltip
                                      label={a.descricaoPendenciaVinculada}
                                      placement="top"
                                      hasArrow
                                    >
                                      <Link
                                        href={`#pendencia-${a.idPendenciaVinculada}`}
                                        display="inline-flex"
                                        alignItems="center"
                                        gap="1"
                                        color="red.500"
                                        fontWeight="medium"
                                      >
                                        <RiErrorWarningLine size={16} />
                                        {a.originalFileName}
                                      </Link>
                                    </Tooltip>
                                  ) : (
                                    a.originalFileName
                                  )}
                                </Td>
                                <Td textAlign="center">
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() =>
                                      baixarArquivoSolicitacao(a.id)
                                    }
                                    isLoading={isRequestingDownloadSolicitacao.includes(
                                      a.id
                                    )}
                                  >
                                    <RiDownloadLine size={18} />
                                  </Button>
                                </Td>
                              </Tr>
                            ))}
                          </Tbody>
                        </Table>
                      </TableContainer>
                    </Box>
                  ))}
                </Flex>
              </Box>
            )}
          </Box>
        </Flex>
      </Flex>
    </Flex>
  );
};

function PendenciaSituacaoInfo({ situacao }: { situacao?: string | null }) {
  const info = getSituacaoPendenciaInfo(situacao);

  return (
    <Flex direction="column" gap="1" pt="2" mb="1">
      <Tag colorScheme={info.colorScheme} w="fit-content">
        <Icon as={info.icon} mr={1} />
        <TagLabel>{info.label}</TagLabel>
      </Tag>
      {info.descricao && (
        <Text fontSize="sm" color="gray.600">
          {info.descricao}
        </Text>
      )}
    </Flex>
  );
}

/** Campo do formulário que o analista liberou para correção (PBI 2576). */
type CampoPendencia = {
  idRespostaSolicitacao: string;
  idSecao: string | null;
  nomeSecao: string | null;
  enunciado: string;
  tipoQuestao: string;
  indiceRepeticao: number;
  valorAtual: string | null;
  justificativa?: string | null;
};

/** Fallback: questão sumiu do tipo de requerimento (reconfigurado depois). */
function CampoCorrecaoLivre({ campo }: { campo: CampoPendencia }) {
  const { register, formState } = useFormContext();

  return (
    <Box w="full">
      <InputText
        label={campo.enunciado}
        name={`${campo.idRespostaSolicitacao}.valorLivre`}
        register={register}
        error={formState.errors[campo.idRespostaSolicitacao] as never}
      />
    </Box>
  );
}

/**
 * Renderiza UM campo a corrigir reaproveitando o `FormFields` do formulário
 * original — é o que garante o requisito do PBI de preservar componente,
 * máscara, obrigatoriedade e opções.
 *
 * O `namePrefix` é o id da RESPOSTA (não o da questão): a mesma questão pode
 * aparecer em várias ocorrências de uma seção repetível, e é a resposta que
 * volta no payload.
 */
function CampoCorrecao({
  campo,
  questao,
  secao
}: {
  campo: CampoPendencia;
  questao?: DetalheQuestao;
  secao?: DetalheSecao;
}) {
  // Questão sumiu do tipo de requerimento (reconfigurado depois da
  // solicitação): cai para texto simples em vez de sumir com o campo.
  if (!questao) {
    return <CampoCorrecaoLivre campo={campo} />;
  }

  return (
    <Box w="full">
      {campo.indiceRepeticao > 0 && (
        <Text fontSize="xs" color="gray.500">
          Ocorrência {campo.indiceRepeticao + 1}
        </Text>
      )}
      <FormFields
        questoes={[questao]}
        namePrefix={`${campo.idRespostaSolicitacao}.`}
        tipoSecaoName={secao?.tipoSecao}
      />
    </Box>
  );
}

function FormPendencia({
  idSolicitacao,
  idPendencia,
  campos = []
}: {
  idSolicitacao: string;
  idPendencia: string;
  campos?: CampoPendencia[];
}) {
  const toast = useToast();
  const [isAnswering, setIsAnswering] = useState(false);
  const dispatch = useAppDispatch();

  const { isRequesting } = useTypedSelector(
    (state) => state.licenciamentourbano.responderPendencia
  );

  const { successPayload: tipoRequerimento } = useTypedSelector(
    (state) => state.licenciamentourbano.obterDetalheTipoRequerimento
  );

  // Só usa a estrutura quando ela é do requerimento certo: o slice é global e
  // guarda um único payload.
  const secoes: DetalheSecao[] = useMemo(
    () => tipoRequerimento?.secoes ?? [],
    [tipoRequerimento]
  );

  const camposResolvidos = useMemo(
    () =>
      campos.map((campo) => {
        const questao = acharQuestao(secoes, campo.idSecao, campo.enunciado);
        const secao =
          secoes.find((sec) => sec.id === campo.idSecao) ??
          secoes.find((sec) =>
            (sec.questoes || []).some((q) => q.id === questao?.id)
          );
        return { campo, questao, secao };
      }),
    [campos, secoes]
  );

  /**
   * Montado por pendência, não como const de módulo: os campos corrigidos mudam
   * a cada pendência e precisam entrar no shape — senão passam sem validação
   * nenhuma (o backend também não valida obrigatoriedade na correção).
   */
  const schemaDinamico = useMemo(() => {
    const shape: Record<string, yup.AnySchema> = {
      texto: yup
        .string()
        .required("Campo obrigatório")
        .max(500, "O texto deve ter no máximo 500 caracteres"),
      files: yup.array().of(yup.mixed().required("O arquivo é obrigatório"))
    };

    camposResolvidos.forEach(({ campo, questao }) => {
      if (!questao?.obrigatoria) return;

      shape[campo.idRespostaSolicitacao] = yup.object({
        [questao.id]: yup
          .mixed()
          .test("obrigatorio", "Campo obrigatório", (valor) => {
            if (Array.isArray(valor)) return valor.length > 0;
            if (valor && typeof valor === "object") return true;
            return !!String(valor ?? "").trim();
          })
      });
    });

    return yup.object(shape);
  }, [camposResolvidos]);

  const methods = useForm<{ [key: string]: any }>({
    resolver: yupResolver(schemaDinamico)
  });

  // Pré-preenche com o valor atual, convertido para o formato do formulário.
  useEffect(() => {
    if (!isAnswering) return;

    const valores: Record<string, ValoresCampo> = {};

    camposResolvidos.forEach(({ campo, questao }) => {
      valores[campo.idRespostaSolicitacao] = questao
        ? parseValorAtual(questao, campo.valorAtual)
        : { valorLivre: campo.valorAtual ?? "" };
    });

    methods.reset({ ...methods.getValues(), ...valores });
  }, [isAnswering, camposResolvidos]);

  const onSubmit: SubmitHandler<any> = (data) => {
    // Só envia id que é GUID de verdade. Se um inválido passar, o backend
    // responde 400 com corpo { "error": "..." } — valor STRING, não array —
    // que o formatError NÃO renderiza: a tela ficaria muda e parecendo travada.
    const idsValidos = filtrarGuidsValidos(
      camposResolvidos.map((c) => c.campo.idRespostaSolicitacao)
    );

    const camposCorrigidos = camposResolvidos
      .filter(({ campo }) => idsValidos.includes(campo.idRespostaSolicitacao))
      .map(({ campo, questao }) => ({
        idRespostaSolicitacao: campo.idRespostaSolicitacao,
        valor: questao
          ? serializarValorCorrigido(
              questao,
              (data[campo.idRespostaSolicitacao] ?? {}) as ValoresCampo
            )
          : String(data[campo.idRespostaSolicitacao]?.valorLivre ?? "")
      }));

    dispatch(
      responderPendencia({
        texto: data.texto,
        files: data.files,
        idSolicitacao: idSolicitacao,
        idPendencia,
        camposCorrigidos: camposCorrigidos.length
          ? camposCorrigidos
          : undefined,
        onSuccess: () => {
          toast({
            title: "Pendência respondida com sucesso",
            status: "success",
            duration: 2000,
            isClosable: true
          });
          methods.reset();
          setIsAnswering(false);
          dispatch(obterDetalheSolicitacao({ idSolicitacao: idSolicitacao }));
        }
      })
    );
  };

  if (!isAnswering) {
    return (
      <Flex w="full" justifyContent="end">
        <Button
          colorScheme="main"
          size="sm"
          onClick={() => setIsAnswering(true)}
        >
          Responder
        </Button>
      </Flex>
    );
  }

  return (
    <FormProvider {...methods}>
      <Box as="form" onSubmit={methods.handleSubmit(onSubmit)} w="full" gap={5}>
        {camposResolvidos.length > 0 && (
          <Box mb="4">
            <Text fontSize="sm" fontWeight="medium" mb="1">
              Campos a corrigir
            </Text>
            <Text fontSize="xs" color="gray.500" mb="2">
              O analista liberou apenas estes campos para edição.
            </Text>
            <VStack spacing={3} align="stretch">
              {camposResolvidos.map(({ campo, questao, secao }) => (
                <CampoCorrecao
                  key={campo.idRespostaSolicitacao}
                  campo={campo}
                  questao={questao}
                  secao={secao}
                />
              ))}
            </VStack>
            <Divider mt="4" />
          </Box>
        )}
        <VStack mb="4">
          <InputText
            label="Mensagem"
            name="texto"
            register={methods.register}
            error={methods.formState.errors["texto"]}
          />
          <InputFileAnexo
            name="files"
            label="Arquivos"
            error={methods.formState.errors["files"]}
          />
        </VStack>
        <Flex w="full" justifyContent="end" gap="2">
          <Button
            variant="solid"
            colorScheme="gray"
            size="sm"
            onClick={() => setIsAnswering(false)}
          >
            Cancelar
          </Button>
          <Button
            colorScheme="main"
            type="submit"
            size="sm"
            isLoading={isRequesting}
          >
            Enviar
          </Button>
        </Flex>
      </Box>
    </FormProvider>
  );
}

function EtapaProcesso({
  etapa,
  titulo,
  data,
  status,
  equipe,
  observacao,
  arquivos,
  situacaoEtapa,
  last = false
}: {
  etapa: number;
  titulo: string;
  data?: string | null;
  equipe?: string;
  observacao?: string;
  status: "concluido" | "atual" | "pendente";
  last?: boolean;
  arquivos?: {
    id: string;
    idResposta: string;
    nome: string;
    originalFileName?: string;
    idSolicitacaoEtapa?: string;
  }[];
  situacaoEtapa?: {
    value: number;
    name: string;
  };
}) {
  const dispatch = useAppDispatch();

  const { isRequesting: isDownloading } = useTypedSelector(
    (state) => state.licenciamentourbano.downloadArquivoEtapaSolicitacao
  );

  function baixarArquivoEtapaSolicitacao(idArquivo: string) {
    dispatch(
      downloadArquivoEtapaSolicitacao({
        idArquivo,
        onSuccess: (arquivo) => {
          directDownloadFileUrl(arquivo.caminho, arquivo.originalFileName);
        }
      })
    );
  }

  const currentOrDone = status == "concluido" || status == "atual";

  return (
    <Box
      pl="8"
      ml="4"
      minH={100}
      pb="6"
      borderLeft="3px"
      borderLeftStyle={!last ? "solid" : undefined}
      borderLeftColor={status == "concluido" ? "green.500" : "gray.300"}
      position="relative"
      alignItems="start"
    >
      <Flex
        position="absolute"
        left={0}
        top={0}
        transform="translateX(-50%)"
        bgColor={currentOrDone ? "green.500" : "gray.200"}
        textColor={currentOrDone ? "white" : "gray.800"}
        h="12"
        w="12"
        borderColor="white"
        borderWidth="6px"
        borderStyle="solid"
        alignItems="center"
        justifyContent="center"
        borderRadius="full"
      >
        {status == "concluido" ? (
          <Text fontSize="xl">
            <RiCheckLine />
          </Text>
        ) : (
          etapa.toString().padStart(2, "0")
        )}
      </Flex>

      {situacaoEtapa && (
        <Box mb="1">
          <StatusRequerimentoTag
            nome={situacaoEtapa.name}
            id={situacaoEtapa.value?.toString()}
            size="sm"
          />
        </Box>
      )}
      <Text
        fontSize="xs"
        fontWeight="semibold"
        color={status == "atual" ? "green.500" : "gray.600"}
        casing="uppercase"
      >
        Etapa {etapa} {status == "concluido" && "- Concluído"}
        {status == "atual" && "- Atual"}
      </Text>

      <Text fontWeight="medium">{titulo}</Text>
      {data && <Text fontSize="sm">Data: {data}</Text>}
      <VStack spacing={1} alignItems="start" mt="0.5">
        {equipe && <Tag variant="outline">{equipe}</Tag>}
        {observacao && (
          <Text fontSize="sm">
            <Text as="span" fontWeight="semibold">
              Obs:{" "}
            </Text>
            {observacao}
          </Text>
        )}
        {arquivos &&
          arquivos.map((f) => (
            <Button
              key={f.id}
              variant="solid"
              colorScheme="gray"
              fontSize="sm"
              py="1"
              w="full"
              justifyContent="start"
              size="sm"
              onClick={() => baixarArquivoEtapaSolicitacao(f.id)}
              isLoading={isDownloading.includes(f.id)}
            >
              <RiDownloadLine size={16} />
              <Text ml="1" as="span">
                {f.originalFileName}
              </Text>
            </Button>
          ))}
      </VStack>
    </Box>
  );
}
